import React, { useCallback, useEffect, useState } from 'react';
import { StarIcon } from 'lucide-react';
import { FormConfig, FormField as FormFieldType, FormResponses } from '../types/form.types';
import FormField from './FormField';
import FormInput from './FormInput';
import FormSelect from './FormSelect';
import FormTextarea from './FormTextarea';
import FormRadio from './FormRadio';
import FormFileUpload from './FormFileUpload';
import { FormTheme } from '../types/formTheme';

interface FormRendererProps {
    formId: string;
    fields: FormFieldType[];
    formConfig?: FormConfig;
    fileFieldIds: string[];
    responses: FormResponses;
    errors: { [fieldId: string]: string };
    visibleFieldIds: string[];
    onFieldChange: (fieldId: string, value: string | number | string[]) => void;
    onFieldBlur?: (fieldId: string) => void;
    onFileUploadStateChange?: (isUploading: boolean) => void;
    appearance?: FormTheme;
    className?: string;
}

const FormRenderer: React.FC<FormRendererProps> = ({
    formId,
    fields,
    formConfig,
    fileFieldIds,
    responses,
    errors,
    visibleFieldIds,
    onFieldChange,
    onFieldBlur,
    onFileUploadStateChange,
    appearance = 'default',
    className = ''
}) => {
    const [uploadingFieldIds, setUploadingFieldIds] = useState<string[]>([]);
    const [hoveredRating, setHoveredRating] = useState<{ fieldId: string; value: number } | null>(null);
    const [hoverSuppressedFieldId, setHoverSuppressedFieldId] = useState<string | null>(null);

    const getNormalizedFieldType = (fieldType: string) => fieldType.trim().toLowerCase();
    const handleFileUploadState = useCallback((fieldId: string, isUploading: boolean) => {
        setUploadingFieldIds((previous) => {
            if (isUploading && !previous.includes(fieldId)) {
                return [...previous, fieldId];
            }
            if (!isUploading && previous.includes(fieldId)) {
                return previous.filter((id) => id !== fieldId);
            }
            return previous;
        });
    }, []);

    useEffect(() => {
        onFileUploadStateChange?.(uploadingFieldIds.length > 0);
    }, [onFileUploadStateChange, uploadingFieldIds]);

    const renderFieldInput = (field: FormFieldType) => {
        const value = responses[field.fieldId] ?? '';
        const stringValue = Array.isArray(value) ? String(value[0] || '') : String(value);
        const error = errors[field.fieldId];
        const normalizedType = getNormalizedFieldType(field.type);
        const commonProps = {
            id: field.fieldId,
            value: stringValue,
            onChange: (newValue: string) => onFieldChange(field.fieldId, newValue),
            onBlur: onFieldBlur ? () => onFieldBlur(field.fieldId) : undefined,
            required: field.required,
            error,
            placeholder: field.placeholder
        };

        switch (normalizedType) {
            case 'text':
                return (
                    <FormInput
                        {...commonProps}
                        type="text"
                        maxLength={field.validation?.maxLength}
                        pattern={field.validation?.pattern}
                        appearance={appearance}
                    />
                );

            case 'email':
                return (
                    <FormInput
                        {...commonProps}
                        type="email"
                        autoComplete="email"
                        appearance={appearance}
                    />
                );

            case 'textarea':
                return (
                    <FormTextarea
                        {...commonProps}
                        maxLength={field.validation?.maxLength}
                        rows={4}
                        autoResize
                        appearance={appearance}
                    />
                );

            case 'select': {
                const selectValue = field.multiple
                    ? Array.isArray(value)
                        ? value.map((item) => String(item))
                        : typeof value === 'string' && value.length > 0
                            ? [value]
                            : []
                    : Array.isArray(value)
                        ? String(value[0] || '')
                        : String(value);
                const otherOption = (field.options || []).find((option) => {
                    const optionValue = option.value.trim().toLowerCase();
                    const optionLabel = option.label.trim().toLowerCase();
                    return optionValue === 'other' || optionLabel === 'other';
                });
                const otherOptionValue = otherOption?.value;
                const allowOther = Boolean(field.validation?.allowOther);
                const isOtherSelected = otherOptionValue
                    ? (
                        Array.isArray(selectValue)
                            ? selectValue.includes(otherOptionValue)
                            : String(selectValue) === otherOptionValue
                    )
                    : false;
                const otherTextFieldId = `${field.fieldId}__other_text`;
                const otherTextValue = responses[otherTextFieldId];
                return (
                    <div className="space-y-3">
                        <FormSelect
                            id={field.fieldId}
                            value={selectValue}
                            onChange={(newValue) => onFieldChange(field.fieldId, newValue)}
                            onBlur={onFieldBlur ? () => onFieldBlur(field.fieldId) : undefined}
                            options={field.options || []}
                            placeholder={field.placeholder}
                            required={field.required}
                            error={error}
                            multiple={Boolean(field.multiple)}
                            appearance={appearance}
                        />

                        {allowOther && isOtherSelected && (
                            <FormInput
                                id={otherTextFieldId}
                                type="text"
                                value={typeof otherTextValue === 'string' ? otherTextValue : ''}
                                onChange={(newValue) => onFieldChange(otherTextFieldId, newValue)}
                                placeholder="Please specify"
                                required={false}
                                error={errors[otherTextFieldId]}
                                appearance={appearance}
                            />
                        )}
                    </div>
                );
            }

            case 'radio': {
                const otherOption = (field.options || []).find((option) => {
                    const optionValue = option.value.trim().toLowerCase();
                    const optionLabel = option.label.trim().toLowerCase();
                    return optionValue === 'other' || optionLabel === 'other';
                });
                const otherOptionValue = otherOption?.value;
                const allowOther = Boolean(field.validation?.allowOther);
                const isOtherSelected = otherOptionValue ? String(value) === otherOptionValue : false;
                const otherTextFieldId = `${field.fieldId}__other_text`;
                const otherTextValue = responses[otherTextFieldId];
                return (
                    <div className="space-y-3">
                        <FormRadio
                            {...commonProps}
                            name={field.fieldId}
                            options={field.options || []}
                            appearance={appearance}
                        />

                        {allowOther && isOtherSelected && (
                            <FormInput
                                id={otherTextFieldId}
                                type="text"
                                value={typeof otherTextValue === 'string' ? otherTextValue : ''}
                                onChange={(newValue) => onFieldChange(otherTextFieldId, newValue)}
                                placeholder="Please specify"
                                required={false}
                                error={errors[otherTextFieldId]}
                                appearance={appearance}
                            />
                        )}
                    </div>
                );
            }

            case 'rating': {
                const isHalfStarScale =
                    !field.options?.length &&
                    field.validation?.min === 0 &&
                    field.validation?.max === 10;
                const ratingOptions = (field.options && field.options.length > 0)
                    ? field.options
                    : [1, 2, 3, 4, 5].map((rating) => ({
                        value: String(rating),
                        label: String(rating)
                    }));
                const useNumericScale = ratingOptions.every((option) => !Number.isNaN(Number(option.value))) && ratingOptions.length > 5;
                const useStarScale = !isHalfStarScale && ratingOptions.every((option) => !Number.isNaN(Number(option.value))) && ratingOptions.length <= 5;
                const selectedValue = Number(value);
                const isHoverSuppressed = hoverSuppressedFieldId === field.fieldId;
                const effectiveRatingValue = !isHoverSuppressed && hoveredRating?.fieldId === field.fieldId
                    ? hoveredRating.value
                    : selectedValue;

                if (isHalfStarScale) {
                    const normalizedScore = Number.isFinite(effectiveRatingValue)
                        ? Math.min(10, Math.max(0, effectiveRatingValue))
                        : 0;
                    const displayStars = normalizedScore / 2;

                    return (
                        <div
                            className="space-y-2"
                            onMouseLeave={() => {
                                if (hoveredRating?.fieldId === field.fieldId) {
                                    setHoveredRating(null);
                                }
                                if (hoverSuppressedFieldId === field.fieldId) {
                                    setHoverSuppressedFieldId(null);
                                }
                            }}
                        >
                            <div className="flex gap-2">
                                {[1, 2, 3, 4, 5].map((starIndex) => {
                                    const scoreForLeftHalf = (starIndex - 1) * 2 + 1;
                                    const scoreForRightHalf = (starIndex - 1) * 2 + 2;
                                    const fillPercent = Math.round(
                                        Math.min(1, Math.max(0, displayStars - (starIndex - 1))) * 100
                                    );

                                    return (
                                        <div key={starIndex} className="relative size-8 overflow-hidden rounded-md bg-gray-300">
                                            {fillPercent > 0 && (
                                                <div
                                                    className="absolute inset-y-0 left-0 bg-green"
                                                    style={{ width: `${fillPercent}%` }}
                                                />
                                            )}
                                            <StarIcon
                                                className="pointer-events-none absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 text-white"
                                                fill="white"
                                                stroke="white"
                                            />
                                            <button
                                                type="button"
                                                className="absolute inset-y-0 left-0 w-1/2"
                                                onMouseEnter={() => {
                                                    if (!isHoverSuppressed) {
                                                        setHoveredRating({ fieldId: field.fieldId, value: scoreForLeftHalf });
                                                    }
                                                }}
                                                onClick={() => {
                                                    onFieldChange(field.fieldId, scoreForLeftHalf);
                                                    setHoveredRating(null);
                                                    setHoverSuppressedFieldId(field.fieldId);
                                                }}
                                                aria-label={`${field.label}: ${scoreForLeftHalf}`}
                                            />
                                            <button
                                                type="button"
                                                className="absolute inset-y-0 right-0 w-1/2"
                                                onMouseEnter={() => {
                                                    if (!isHoverSuppressed) {
                                                        setHoveredRating({ fieldId: field.fieldId, value: scoreForRightHalf });
                                                    }
                                                }}
                                                onClick={() => {
                                                    onFieldChange(field.fieldId, scoreForRightHalf);
                                                    setHoveredRating(null);
                                                    setHoverSuppressedFieldId(field.fieldId);
                                                }}
                                                aria-label={`${field.label}: ${scoreForRightHalf}`}
                                            />
                                        </div>
                                    );
                                })}
                            </div>

                            <button
                                type="button"
                                onClick={() => {
                                    onFieldChange(field.fieldId, 0);
                                    setHoveredRating(null);
                                    if (hoverSuppressedFieldId === field.fieldId) {
                                        setHoverSuppressedFieldId(null);
                                    }
                                }}
                                className="text-xs font-medium text-gray-600 underline underline-offset-2 hover:text-black"
                                aria-label={`Clear ${field.label}`}
                            >
                                Clear (set to 0)
                            </button>
                        </div>
                    );
                }

                return (
                    <div
                        className={useNumericScale ? 'grid grid-cols-5 sm:grid-cols-10 gap-2' : 'flex gap-2 flex-wrap'}
                        onMouseLeave={() => {
                            if (useStarScale && hoveredRating?.fieldId === field.fieldId) {
                                setHoveredRating(null);
                            }
                            if (hoverSuppressedFieldId === field.fieldId) {
                                setHoverSuppressedFieldId(null);
                            }
                        }}
                    >
                        {ratingOptions.map((option) => {
                            const optionNumber = Number(option.value);
                            const optionValue = Number.isNaN(optionNumber) ? option.value : optionNumber;
                            const isSelected = String(value) === String(option.value);
                            const isHighlighted = Number.isNaN(optionNumber)
                                ? isSelected
                                : (useStarScale ? effectiveRatingValue : selectedValue) >= optionNumber;

                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() => {
                                        onFieldChange(field.fieldId, optionValue);
                                        if (useStarScale) {
                                            setHoveredRating(null);
                                            setHoverSuppressedFieldId(field.fieldId);
                                        }
                                    }}
                                    onMouseEnter={() => {
                                        if (useStarScale && !isHoverSuppressed && !Number.isNaN(optionNumber)) {
                                            setHoveredRating({ fieldId: field.fieldId, value: optionNumber });
                                        }
                                    }}
                                    className={`
                                        transition-all duration-200
                                        ${useNumericScale
                                            ? `
                                                rounded-lg border px-3 py-2 text-sm font-semibold
                                                ${isSelected
                                                    ? 'border-black bg-black text-white'
                                                    : 'border-gray-300 text-gray-700 hover:border-black'
                                                }
                                            `
                                            : useStarScale
                                                ? 'relative size-8 overflow-hidden rounded-md bg-gray-300'
                                            : `
                                                text-3xl
                                                ${isHighlighted
                                                    ? appearance === 'tracking' ? 'text-orange scale-110' : 'text-yellow-400 scale-110'
                                                    : appearance === 'tracking' ? 'text-gray-300 hover:text-orange/40' : 'text-gray-300 hover:text-yellow-200'
                                                }
                                            `
                                        }
                                    `}
                                    aria-label={`${field.label}: ${option.label}`}
                                    aria-pressed={isSelected}
                                >
                                    {useNumericScale ? (
                                        option.label
                                    ) : useStarScale ? (
                                        <>
                                            {isHighlighted && (
                                                <div className="absolute inset-y-0 left-0 bg-green" style={{ width: '100%' }} />
                                            )}
                                            <StarIcon
                                                className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 text-white"
                                                fill="white"
                                                stroke="white"
                                            />
                                        </>
                                    ) : (
                                        '⭐'
                                    )}
                                </button>
                            );
                        })}
                    </div>
                );
            }

            case 'file':
                return (
                    <FormFileUpload
                        formId={formId}
                        field={field}
                        value={value}
                        responses={responses}
                        fileFieldIds={fileFieldIds}
                        formConfig={formConfig}
                        onChange={(newValue) => onFieldChange(field.fieldId, newValue)}
                        onUploadStateChange={handleFileUploadState}
                        appearance={appearance}
                    />
                );

            case 'statictext':
            case 'static_text':
                return (
                    <div className="text-sm leading-relaxed text-gray-600">
                        {field.content || ''}
                    </div>
                );

            default:
                return (
                    <div className="text-sm text-red-500">
                        Unsupported field type: {field.type}
                    </div>
                );
        }
    };

    return (
        <div className={`space-y-6 ${className}`}>
            {fields.map((field) => {
                // Only render visible fields
                if (!visibleFieldIds.includes(field.fieldId)) {
                    return null;
                }

                const normalizedType = getNormalizedFieldType(field.type);
                if (normalizedType === 'statictext' || normalizedType === 'static_text') {
                    return (
                        <div
                            key={field.fieldId}
                            className="animate-fade-in"
                        >
                            {renderFieldInput(field)}
                        </div>
                    );
                }

                return (
                    <FormField
                        key={field.fieldId}
                        id={field.fieldId}
                        label={field.label}
                        required={field.required}
                        error={errors[field.fieldId]}
                        className="animate-fade-in"
                    >
                        {renderFieldInput(field)}
                    </FormField>
                );
            })}
        </div>
    );
};

export default FormRenderer;
